'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/app/utils';

interface CenterPeekCarouselProps {
  items: ReactNode[];
  className?: string;
}

export const CenterPeekCarousel = ({ items, className }: CenterPeekCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 50;

  // 计算每个slide的宽度（包含40px间隙）
  const slideWidth = '160px';
  const gap = '40px';

  // 计算transform值，让当前slide居中
  const getTransform = () => {
    // 计算当前slide之前所有slide的宽度和间距
    const slideWidthPx = 160;
    const gapPx = 40;
    const totalWidth = slideWidthPx + gapPx;
    const beforeSlidesWidth = currentIndex * totalWidth;

    // 计算居中需要的偏移量：(容器宽度 - slide宽度) / 2
    const centerOffset = `calc((100% - ${slideWidthPx}px) / 2)`;

    // 拖动时的额外偏移
    const dragOffsetPx = isDragging ? dragOffset : 0;

    return `translateX(calc(-${beforeSlidesWidth}px + ${centerOffset} + ${dragOffsetPx}px))`;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setDragOffset(0);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: globalThis.MouseEvent) => {
      e.preventDefault();
      const offset = e.clientX - dragStart;
      setDragOffset(offset);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      const distance = dragOffset;
      if (Math.abs(distance) > minSwipeDistance) {
        if (distance < 0 && currentIndex < items.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (distance > 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
      }
      setDragOffset(0);
      setDragStart(0);
    };

    const handleTouchMove = (e: globalThis.TouchEvent) => {
      const offset = e.touches[0].clientX - dragStart;
      setDragOffset(offset);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      const distance = dragOffset;
      if (Math.abs(distance) > minSwipeDistance) {
        if (distance < 0 && currentIndex < items.length - 1) {
          setCurrentIndex(currentIndex + 1);
        } else if (distance > 0 && currentIndex > 0) {
          setCurrentIndex(currentIndex - 1);
        }
      }
      setDragOffset(0);
      setDragStart(0);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart, dragOffset, currentIndex, items.length]);

  // 根据数量和当前索引决定遮挡显示
  const showLeftMask = items.length > 1 && currentIndex > 0;
  const showRightMask = items.length > 1 && currentIndex < items.length - 1;

  return (
    <div className={cn('relative size-full', className)}>
      <div
        className={cn(
          'relative flex h-full flex-col overflow-hidden',
          showLeftMask &&
            "before:-translate-y-1/2 before:pointer-events-none before:absolute before:top-1/2 before:left-0 before:z-10 before:h-[160px] before:w-[68px] before:bg-[linear-gradient(90deg,#0A0D10_0%,rgba(10,13,16,0.00)_100%)] before:content-['']",
          showRightMask &&
            "after:-translate-y-1/2 after:pointer-events-none after:absolute after:top-1/2 after:right-0 after:z-10 after:h-[160px] after:w-[68px] after:bg-[linear-gradient(270deg,#0A0D10_0%,rgba(10,13,16,0.00)_100%)] after:content-['']",
        )}
        ref={containerRef}
      >
        <div
          className={cn(
            'flex flex-1 cursor-grab items-center active:cursor-grabbing',
            !isDragging && 'transition-transform duration-300 ease-in-out',
          )}
          style={{
            transform: getTransform(),
            touchAction: 'pan-x',
            userSelect: 'none',
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {items.map((item, index) => {
            const itemKey =
              typeof item === 'object' && item !== null && 'key' in item ? item.key : `carousel-item-${index}`;
            return (
              <div
                key={itemKey}
                className="flex flex-shrink-0 items-center justify-center"
                style={{
                  width: slideWidth,
                  marginRight: index < items.length - 1 ? gap : '0',
                }}
              >
                {item}
              </div>
            );
          })}
        </div>

        <div className="-translate-x-1/2 absolute bottom-[28px] left-1/2 flex gap-2">
          {items.map((item, index) => {
            const itemKey =
              typeof item === 'object' && item !== null && 'key' in item ? item.key : `indicator-${index}`;
            return (
              <button
                key={itemKey}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  index === currentIndex ? 'w-4 bg-system-white' : 'w-1.5 bg-system-white/40',
                )}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
